/**
 * API Client Service
 *
 * Provides a simplified interface for making HTTP requests using Axios.
 * All requests automatically include Bearer token authentication via interceptors.
 */

import axiosInstance from './axios'
import type { AxiosRequestConfig } from 'axios'

/**
 * API Client object with HTTP methods
 */
export const apiClient = {
    /**
     * Perform a GET request
     *
     * @template T - The expected response data type
     * @param url - The endpoint URL (relative to base URL)
     * @param config - Optional Axios request configuration
     * @returns Promise resolving to the response data
     *
     * @example
     * ```typescript
     * const user = await apiClient.get<User>('/api/users/123')
     * ```
     */
    async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await axiosInstance.get<T>(url, config)
        return response.data
    },

    /**
     * Perform a POST request
     *
     * @template T - The expected response data type
     * @param url - The endpoint URL (relative to base URL)
     * @param data - The request body data
     * @param config - Optional Axios request configuration
     * @returns Promise resolving to the response data
     *
     * @example
     * ```typescript
     * const newUser = await apiClient.post<User>('/api/users', { name: 'John' })
     * ```
     */
    async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        const response = await axiosInstance.post<T>(url, data, config)
        return response.data
    },

    /**
     * Perform a PUT request
     *
     * @template T - The expected response data type
     * @param url - The endpoint URL (relative to base URL)
     * @param data - The request body data
     * @param config - Optional Axios request configuration
     * @returns Promise resolving to the response data
     *
     * @example
     * ```typescript
     * const updatedUser = await apiClient.put<User>('/api/users/123', { name: 'Jane' })
     * ```
     */
    async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        const response = await axiosInstance.put<T>(url, data, config)
        return response.data
    },

    /**
     * Perform a DELETE request
     *
     * @template T - The expected response data type
     * @param url - The endpoint URL (relative to base URL)
     * @param config - Optional Axios request configuration
     * @returns Promise resolving to the response data
     *
     * @example
     * ```typescript
     * await apiClient.delete('/api/users/123')
     * ```
     */
    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await axiosInstance.delete<T>(url, config)
        return response.data
    }
}
